import type { ComponentProps } from 'react';
import { PortIconSVG } from 'atto-svgs';

import { colors, darkThemeSelector, keyframes, styled } from '../../stitches.config';
import { SlotEmptyBay } from '../Slots/atoms/SlotEmptyBay';

export type FanStatus = 'running' | 'stopped' | 'failed' | 'empty';

export type FanSpeed = 'slow' | 'normal' | 'fast';

const fanSpin = keyframes({
  to: { transform: 'rotate(360deg)' },
});

// Spins a box around the icon, not the svg: Chrome won't run an svg's transform animation on the
// compositor, so every main-thread stall would freeze the fan and then snap it forward.
const FanRotor = styled('div', {
  display: 'flex',
  animationName: `${fanSpin}`,
  animationTimingFunction: 'linear',
  animationIterationCount: 'infinite',

  '@media (prefers-reduced-motion: reduce)': {
    animation: 'none',
  },

  variants: {
    speed: {
      slow: { animationDuration: '2.4s' },
      normal: { animationDuration: '1.2s' },
      fast: { animationDuration: '0.6s' },
    },
    // Paused rather than removed, so a fan that stops holds the angle it stopped at.
    spinning: {
      true: {},
      false: { animationPlayState: 'paused' },
    },
  },

  defaultVariants: {
    speed: 'normal',
  },
});
FanRotor.displayName = 'FanRotor';

const FanContainer = styled('div', {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  width: '$24',
  height: '$20',

  variants: {
    status: {
      running: {
        color: colors.hardwareStrokePositiveLight,

        [darkThemeSelector]: {
          color: colors.hardwareStrokePositiveDark,
        },
      },
      stopped: {
        color: colors.hardwareStrokeNeutralLight,

        [darkThemeSelector]: {
          color: colors.hardwareStrokeNeutralDark,
        },
      },
      failed: {
        color: colors.hardwareStrokeNegativeLight,

        [darkThemeSelector]: {
          color: colors.hardwareStrokeNegativeDark,
        },
      },
      empty: {
        color: colors.hardwareStrokeNeutralLight,

        [darkThemeSelector]: {
          color: colors.hardwareStrokeNeutralDark,
        },
      },
    },
  },
});
FanContainer.displayName = 'FanContainer';

const fanStatusLabels: Record<FanStatus, string> = {
  running: 'Fan running',
  stopped: 'Fan stopped',
  failed: 'Fan failed',
  empty: 'Empty fan bay',
};

/** Tiers a reading against the fan's rated maximum. Without one there is nothing to tier against. */
export function getFanSpeed(speedRpm: number, maxRpm?: number): FanSpeed {
  if (!maxRpm) return 'normal';

  const ratio = speedRpm / maxRpm;
  if (ratio < 0.4) return 'slow';
  if (ratio < 0.75) return 'normal';
  return 'fast';
}

// A real fan turns ~100 times a second, which a 60 Hz display aliases into a backwards wobble.
const RPM_SCALE = 100;

/** One turn's duration for a reading slowed by `RPM_SCALE`, so faster fans still spin faster. */
export function getFanTurnDuration(speedRpm: number): string {
  return `${((60 * RPM_SCALE) / speedRpm).toFixed(3)}s`;
}

const rpmFormat = new Intl.NumberFormat('en-US');

export function formatFanSpeed(speedRpm: number): string {
  return `${rpmFormat.format(speedRpm)} RPM`;
}

export type FanProps = Omit<ComponentProps<typeof FanContainer>, 'status'> & {
  /** Accessible name. Defaults to one describing the status. */
  label?: string;
  /** How fast the rotor turns while running. See `getFanSpeed`. */
  speed?: FanSpeed;
  /** Spins the rotor in proportion to this reading instead, overriding `speed`. */
  speedRpm?: number;
  status: FanStatus;
};

/**
 * A fan module as a faceplate glyph. A running fan's blades turn; a stopped or failed one holds
 * still, and an empty bay draws an outline with nothing in it.
 */
export function Fan({ label, speed, speedRpm, status, ...remaining }: FanProps) {
  return (
    <FanContainer
      {...remaining}
      role="img"
      aria-label={label ?? fanStatusLabels[status]}
      status={status}
    >
      {status === 'empty' ? (
        <SlotEmptyBay />
      ) : (
        <FanRotor
          speed={speed}
          spinning={status === 'running'}
          style={speedRpm ? { animationDuration: getFanTurnDuration(speedRpm) } : undefined}
        >
          <PortIconSVG.Fan aria-hidden />
        </FanRotor>
      )}
    </FanContainer>
  );
}
